import type { FC, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import theme from '../theme.ts';
import store from '../redux/store.ts';
import { BrowserRouter } from 'react-router';
import { ThemeProvider } from '@mui/material/styles';
import { Provider } from 'react-redux';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import styleCache from './header/styleContent.ts';

import { AgentProvider } from "@/context/agent/AgentProvider.tsx";

const ShadowRootContext = createContext<ShadowRoot | null>(null);

export const useShadowRoot = () => useContext(ShadowRootContext);

export const ShadowRootProvider = ({ children, root }: { children: React.ReactNode; root: ShadowRoot }) => {
  return <ShadowRootContext.Provider value={root}>{children}</ShadowRootContext.Provider>;
};

const ShadowComponent: FC<PropsWithChildren> = ({ children }) => {
  const shadowRef = useRef<HTMLElement | null>(null);

  const applyConstructableStylesheet = (shadowRoot: ShadowRoot) => {
    shadowRoot.adoptedStyleSheets = [...document.styleSheets]
      .map(sheet => {
        try {
          const newSheet = new CSSStyleSheet();
          newSheet.replaceSync([...sheet.cssRules].map(rule => rule.cssText).join('\n'));
          return newSheet;
        } catch (e) {
          return null;
        }
      })
      .filter(Boolean) as CSSStyleSheet[];
  };

  useEffect(() => {
    if (shadowRef.current && !shadowRef.current.shadowRoot) {
      let shadowRoot: ShadowRoot | null = shadowRef.current.shadowRoot;
      if (!shadowRoot) {
        shadowRoot = shadowRef.current.attachShadow({ mode: 'open', delegatesFocus: true });
      }

      applyConstructableStylesheet(shadowRoot);
      const styleContainer = document.createElement('style');
      styleContainer.textContent = styleCache;
      shadowRoot.appendChild(styleContainer);

      const reactContainer = document.createElement('div');
      reactContainer.id = 'shadow-container';
      shadowRoot.appendChild(reactContainer);

      const cache = createCache({
        key: 'mui',
        container: styleContainer,
      });

      const reactRoot = createRoot(reactContainer);
      reactRoot.render(
        <CacheProvider value={cache}>
          <ThemeProvider theme={theme}>
            <Provider store={store}>
              <BrowserRouter>
                <AgentProvider>
                  <ShadowRootProvider root={shadowRoot}>{children}</ShadowRootProvider>
                </AgentProvider>
              </BrowserRouter>
            </Provider>
          </ThemeProvider>
        </CacheProvider>,
      );
    }
  }, []);

  return <span ref={shadowRef} style={{ display: 'contents' }} />;
};

export default ShadowComponent;
